import { useCallback, useState } from 'react';
import { notify } from '@/lib/notify';
import type { StickerSetInfo, StickerInfo, SendStickerData } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function useStickers() {
  const [stickerSets, setStickerSets] = useState<StickerSetInfo[]>([]);
  const [recentStickers, setRecentStickers] = useState<StickerInfo[]>([]);
  const [currentSetStickers, setCurrentSetStickers] = useState<StickerInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSet, setIsLoadingSet] = useState(false);
  const [isSending, setIsSending] = useState(false);

  /**
   * Загрузить все стикерсеты пользователя
   */
  const fetchStickerSets = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/stickers`);

      const result: ApiResponse<{ sets: StickerSetInfo[] }> = await response.json();
      if (result.success && result.data) {
        setStickerSets(result.data?.sets ?? []);
      }
    } catch (_error) {
      notify.error('Ошибка загрузки стикерсетов');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Загрузить недавно использованные стикеры
   */
  const fetchRecentStickers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/stickers/recent`);

      const result: ApiResponse<StickerInfo[]> = await response.json();
      if (result.success && result.data) {
        setRecentStickers(result.data);
      }
    } catch (_error) {
      notify.error('Ошибка загрузки недавних стикеров');
    }
  }, []);

  /**
   * Загрузить стикеры из набора
   */
  const fetchSetStickers = useCallback(async (setId: string, accessHash: string) => {
    setIsLoadingSet(true);
    try {
      const response = await fetch(
        `${API_URL}/api/telegram-client/stickers/set/${setId}?accessHash=${encodeURIComponent(accessHash)}`,
      );

      const result: ApiResponse<StickerInfo[]> = await response.json();
      if (result.success && result.data) {
        setCurrentSetStickers(result.data);
        return result.data;
      }
      return [];
    } catch (_error) {
      notify.error('Ошибка загрузки стикеров набора');
      return [];
    } finally {
      setIsLoadingSet(false);
    }
  }, []);

  /**
   * Отправить стикер в чат
   */
  const sendSticker = useCallback(async (chatId: number, data: SendStickerData): Promise<boolean> => {
    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/stickers/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result: ApiResponse<unknown> = await response.json();
      if (!result.success) {
        notify.error(result.error || 'Ошибка отправки стикера');
        return false;
      }
      return true;
    } catch (_error) {
      notify.error('Ошибка сети');
      return false;
    } finally {
      setIsSending(false);
    }
  }, []);

  /**
   * Получить URL превью стикера
   */
  const getStickerThumbUrl = useCallback((sticker: StickerInfo): string => {
    const params = new URLSearchParams({
      accessHash: sticker.accessHash,
      fileReference: sticker.fileReference,
    });
    return `${API_URL}/api/telegram-client/stickers/thumb/${sticker.id}?${params.toString()}`;
  }, []);

  return {
    stickerSets,
    recentStickers,
    currentSetStickers,
    isLoading,
    isLoadingSet,
    isSending,
    fetchStickerSets,
    fetchRecentStickers,
    fetchSetStickers,
    sendSticker,
    getStickerThumbUrl,
  };
}
